import React, { useEffect, useState } from 'react';
import { Bell, BellRinging, AppleLogo, DeviceMobile } from '@phosphor-icons/react';
import { toast } from 'sonner';
import {
  isPushSupported,
  isIOS,
  isStandalonePWA,
  getPushStatus,
  enablePush,
  disablePush,
  sendTestPush,
} from '../utils/push';

export default function PushSettingsCard() {
  const [supported, setSupported] = useState(true);
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [devices, setDevices] = useState(0);
  const [iosNoPwa, setIosNoPwa] = useState(false);

  useEffect(() => {
    setSupported(isPushSupported());
    setIosNoPwa(isIOS() && !isStandalonePWA());
    (async () => {
      const s = await getPushStatus();
      setEnabled(!!s.enabled);
      setDevices(s.devices || 0);
    })();
  }, []);

  const handleEnable = async () => {
    setLoading(true);
    try {
      await enablePush();
      const s = await getPushStatus();
      setEnabled(!!s.enabled);
      setDevices(s.devices || 0);
      toast.success('Benachrichtigungen aktiviert');
    } catch (e) {
      toast.error(e.message || 'Konnte nicht aktivieren');
    } finally {
      setLoading(false);
    }
  };

  const handleDisable = async () => {
    setLoading(true);
    try {
      await disablePush();
      const s = await getPushStatus();
      setEnabled(!!s.enabled);
      setDevices(s.devices || 0);
      toast.success('Benachrichtigungen deaktiviert');
    } catch (e) {
      toast.error('Konnte nicht deaktivieren');
    } finally {
      setLoading(false);
    }
  };

  const handleTest = async () => {
    try {
      const r = await sendTestPush();
      if (r.delivered > 0) toast.success('Test-Benachrichtigung gesendet');
      else toast.error('Keine aktive Subscription gefunden');
    } catch (e) {
      toast.error('Test fehlgeschlagen');
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-[#D6D3D1] p-6" data-testid="push-settings-card">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-[#FDF0ED] flex items-center justify-center flex-shrink-0">
          <BellRinging size={22} weight="fill" className="text-[#AB1C24]" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-heading font-semibold text-lg text-[#292524]">Benachrichtigungen</h3>
          <p className="text-sm text-[#78716C]">Tägliches Thema, neue Klassen, Materialien und Streak-Erinnerungen.</p>
        </div>
      </div>

      {!supported && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900" data-testid="push-unsupported">
          Dein Browser unterstützt keine Web-Push-Benachrichtigungen.
        </div>
      )}

      {supported && iosNoPwa && (
        <div className="rounded-lg bg-blue-50 border border-blue-200 p-4 text-sm text-blue-900 space-y-2" data-testid="push-ios-pwa-hint">
          <div className="flex items-center gap-2 font-medium">
            <AppleLogo size={18} weight="fill" />
            Auf iPhone: App installieren
          </div>
          <ol className="list-decimal pl-5 space-y-1 text-[13px]">
            <li>Tippe unten in Safari auf das Teilen-Symbol.</li>
            <li>Wähle <strong>Zum Home-Bildschirm</strong>.</li>
            <li>Öffne die App vom Home-Bildschirm und aktiviere hier die Benachrichtigungen.</li>
          </ol>
        </div>
      )}

      {supported && !iosNoPwa && (
        <div className="flex flex-wrap items-center gap-3">
          {enabled ? (
            <>
              <span className="inline-flex items-center gap-1.5 text-sm font-medium text-green-700 bg-green-50 border border-green-200 rounded-full px-3 py-1" data-testid="push-status-on">
                <Bell size={14} weight="fill" /> Aktiv · {devices} {devices === 1 ? 'Gerät' : 'Geräte'}
              </span>
              <button
                type="button"
                onClick={handleDisable}
                disabled={loading}
                className="text-sm text-[#AB1C24] hover:underline disabled:opacity-50"
                data-testid="push-disable-btn"
              >
                Deaktivieren
              </button>
              <button
                type="button"
                onClick={handleTest}
                disabled={loading}
                className="text-sm text-[#57534E] hover:text-[#292524] hover:underline disabled:opacity-50 ml-auto"
                data-testid="push-test-btn"
              >
                Test senden
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={handleEnable}
              disabled={loading}
              className="inline-flex items-center gap-2 bg-[#AB1C24] hover:bg-[#8A151C] text-white text-sm font-medium rounded-lg px-4 py-2 disabled:opacity-50 transition-colors"
              data-testid="push-enable-btn"
            >
              <DeviceMobile size={16} weight="fill" />
              Benachrichtigungen aktivieren
            </button>
          )}
        </div>
      )}
    </div>
  );
}
